import React, { useState, useEffect } from 'react';
import { Lock, CheckCircle, Clock, ShieldCheck, Download, Award, Loader2, BookOpen } from 'lucide-react';
import { useCourseAccess } from '../hooks/useCourseAccess';
import { useReaderAuth } from '../context/ReaderAuthContext';
import jsPDF from 'jspdf';
import { PILLAR_ICON_URLS } from './PillarIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_ICONS = [
  { url: PILLAR_ICON_URLS.selfAnchoring,         alt: 'Self-Anchoring icon — anchor symbol representing grounded identity and internal coherence' },
  { url: PILLAR_ICON_URLS.perspectiveShifting,   alt: 'Perspective-Shifting icon — magnifying glass representing cultural insight and perspective-taking' },
  { url: PILLAR_ICON_URLS.adaptiveCommunication, alt: 'Adaptive Communication icon — dialogue symbol representing communication across difference' },
  { url: PILLAR_ICON_URLS.relationalIntegration, alt: 'Relational Integration icon — interlocking puzzle symbol representing shared space and connection' },
];

const TCL_ICON_URL   = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/c4h0snj7_Third%20culture%20Leadership%20icon.png';
const TCL_BANNER_URL = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/jxiucnvi_Third%20culture%20leadership%20banner.webp';

const STRIPE_FALLBACK_LINKS = {
  'module-2': 'https://buy.stripe.com/cNi7sK71n0eDg3HahJ8so05',
  'module-3': 'https://buy.stripe.com/fZueVcadz1iH7xb9dF8so03',
  'module-4': 'https://buy.stripe.com/dRm8wO5Xje5tdVz0H98so04',
};

/** Load any image URL as a base64 data URL for PDF embedding. */
const loadImg = (url) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth || 400;
      c.height = img.naturalHeight || 200;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve({ data: c.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

/** Generate and download the landscape A4 completion certificate. */
const generateCertificate = async (user, accessibleModules) => {
  const pdf = new jsPDF('l', 'mm', 'a4');
  const pw = 297; const ph = 210;

  const [icon, banner] = await Promise.all([loadImg(TCL_ICON_URL), loadImg(TCL_BANNER_URL)]);

  // ── Navy header ──
  pdf.setFillColor(7, 27, 58);
  pdf.rect(0, 0, pw, 48, 'F');

  // Banner centred in header
  if (banner) {
    const bh = 22; const bw = bh * (banner.w / banner.h);
    try { pdf.addImage(banner.data, 'PNG', pw / 2 - bw / 2, 13, bw, bh); } catch {}
  }
  // Small icon top-right corner of header
  if (icon) {
    try { pdf.addImage(icon.data, 'PNG', pw - 20, 11, 14, 14); } catch {}
  }

  // ── Double gold rule ──
  pdf.setDrawColor(201, 147, 22);
  pdf.setLineWidth(0.5);
  pdf.line(20, 51, pw - 20, 51);
  pdf.setLineWidth(0.2);
  pdf.line(20, 54, pw - 20, 54);

  // ── Body ──
  pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(160, 140, 100);
  pdf.text('THIRD CULTURE LEADERSHIP', pw / 2, 65, { align: 'center' });

  pdf.setFontSize(26); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(7, 27, 58);
  pdf.text('CERTIFICATE OF COMPLETION', pw / 2, 78, { align: 'center' });

  pdf.setDrawColor(220, 200, 170); pdf.setLineWidth(0.3);
  pdf.line(pw / 2 - 90, 83, pw / 2 + 90, 83);

  pdf.setFontSize(11); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(110, 110, 110);
  pdf.text('This certifies that', pw / 2, 93, { align: 'center' });

  const recipientName = user?.name || 'Course Participant';
  pdf.setFontSize(24); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(7, 27, 58);
  pdf.text(recipientName, pw / 2, 107, { align: 'center' });

  // Gold underline under name
  const nw = pdf.getTextWidth(recipientName);
  pdf.setDrawColor(201, 147, 22); pdf.setLineWidth(0.6);
  pdf.line(pw / 2 - nw / 2, 110, pw / 2 + nw / 2, 110);

  pdf.setFontSize(11); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(110, 110, 110);
  pdf.text('has successfully completed', pw / 2, 120, { align: 'center' });

  pdf.setFontSize(16); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(7, 27, 58);
  pdf.text('Cultural Fluency Mini-Course', pw / 2, 131, { align: 'center' });

  // ── Module list (two-column layout) ──
  const mods = accessibleModules.map(m => m.title.replace(/^Module \d+: /, ''));
  pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80, 80, 80);
  const colL = pw / 2 - 82; const colR = pw / 2 + 4;
  if (mods[0]) pdf.text('\u2713  ' + mods[0], colL, 143);
  if (mods[1]) pdf.text('\u2713  ' + mods[1], colL, 150);
  if (mods[2]) pdf.text('\u2713  ' + mods[2], colR, 143);
  if (mods[3]) pdf.text('\u2713  ' + mods[3], colR, 150);

  pdf.setDrawColor(220, 200, 170); pdf.setLineWidth(0.3);
  pdf.line(pw / 2 - 90, 157, pw / 2 + 90, 157);

  // ── Date ──
  pdf.setFontSize(9.5); pdf.setTextColor(120, 120, 120);
  const dateStr = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  pdf.text('Issued: ' + dateStr, pw / 2, 165, { align: 'center' });

  // ── Founder signature ──
  pdf.setFontSize(13); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(7, 27, 58);
  pdf.text('Dr Promise Monday', pw / 2, 177, { align: 'center' });
  pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(120, 120, 120);
  pdf.text('Paediatrician \u2022 Author \u2022 Founder, Third Culture Leadership', pw / 2, 182, { align: 'center' });

  // ── Navy footer ──
  pdf.setFillColor(7, 27, 58);
  pdf.rect(0, ph - 14, pw, 14, 'F');
  pdf.setFontSize(7.5); pdf.setTextColor(255, 255, 255);
  pdf.text('Third Culture Leadership  \u00B7  Cultural Fluency Mini-Course  \u00B7  thirdcultureleadership.com', pw / 2, ph - 6, { align: 'center' });
  if (icon) { try { pdf.addImage(icon.data, 'PNG', pw - 16, ph - 13, 10, 10); } catch {} }

  pdf.save('TCL-Certificate-' + recipientName.replace(/\s+/g, '-') + '-' + new Date().toISOString().split('T')[0] + '.pdf');
};

const modules = [
  { id: 1, title: 'Module 1: Self-Anchoring',             description: 'Establishing your cultural identity as a stable leadership foundation.',            duration: '45 mins', lessons: 5, isFree: true  },
  { id: 2, title: 'Module 2: Perspective-Shifting',        description: 'Developing the ability to see situations through multiple cultural lenses.',         duration: '60 mins', lessons: 6, isFree: false },
  { id: 3, title: 'Module 3: Adaptive Communication',      description: 'Expressing clearly across cultures without losing your voice.',                      duration: '55 mins', lessons: 5, isFree: false },
  { id: 4, title: 'Module 4: Relational Integration',      description: 'Building trust, influence, and cohesion across cultural boundaries.',               duration: '50 mins', lessons: 5, isFree: false },
];

const CourseDashboard = ({ user, onLogout }) => {
  const { hasAccess: hasCourseAccess, isLoading: isAccessLoading, accessType, purchasedModules, refetchAccess } = useCourseAccess();
  const { token } = useReaderAuth();
  const [isGenerating, setIsGenerating]     = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // module-id string while loading
  const [completedIds, setCompletedIds]     = useState(new Set());

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/modules/completions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.completions) {
          setCompletedIds(new Set(d.completions.map(c => c.module_number)));
        }
      })
      .catch(() => {});
  }, [token]);

  const isWhitelisted = accessType === 'whitelisted';

  const isModuleAccessible = (module) => {
    if (module.isFree) return true;
    if (isWhitelisted || hasCourseAccess) return true;
    return purchasedModules.includes('module-' + module.id);
  };

  const accessibleModules = modules.filter(isModuleAccessible);
  const allAccessible = accessibleModules.length === modules.length;
  const progressPct = (accessibleModules.length / modules.length) * 100;

  const handleDownloadCertificate = async () => {
    setIsGenerating(true);
    try { await generateCertificate(user, accessibleModules); } catch (e) { console.error(e); }
    setIsGenerating(false);
  };

  const handleStartModule = (moduleId) => {
    window.location.href = '/module-' + moduleId;
  };

  const handlePurchaseModule = async (moduleId) => {
    const mid = `module-${moduleId}`;
    setCheckoutLoading(mid);
    try {
      const res = await fetch(`${API_URL}/api/reader/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ module_id: mid }),
      });
      if (res.ok) {
        const { checkout_url } = await res.json();
        window.location.href = checkout_url;
      } else {
        // Fallback to static link if dynamic checkout fails
        window.open(STRIPE_FALLBACK_LINKS[mid] || '#', '_blank');
      }
    } catch {
      window.open(STRIPE_FALLBACK_LINKS[mid] || '#', '_blank');
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3] pt-32 pb-20 px-5 sm:px-8" data-testid="course-dashboard">
      <div className="max-w-4xl mx-auto">

        {/* ── Dashboard Header ── */}
        <div className="bg-white border border-navy/[0.07] rounded-2xl p-8 mb-6 shadow-sm shadow-navy/[0.03]">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold/70 font-sans mb-2">Self-Paced Learning</p>
              <h1 className="font-serif text-3xl sm:text-4xl text-navy font-light tracking-tight mb-1">
                Cultural Fluency Mini-Course
              </h1>
              <p className="text-navy/40 text-sm mb-3">
                Welcome back, <span className="font-medium text-navy/70">{user?.name}</span>
              </p>
              {isWhitelisted && (
                <span className="inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-500/10 border border-green-500/20 px-2.5 py-1 rounded-full">
                  <CheckCircle size={12} /> Full access granted
                </span>
              )}
            </div>
            <button
              onClick={onLogout}
              className="text-navy/30 hover:text-navy/60 text-sm transition-colors"
            >
              Sign out
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-navy/35 mb-2">
              <span>Modules accessible</span>
              <span>{accessibleModules.length} of {modules.length}</span>
            </div>
            <div className="h-1.5 bg-navy/[0.07] rounded-full overflow-hidden">
              <div
                className="h-full bg-gold rounded-full transition-all duration-500"
                style={{ width: progressPct + '%' }}
              />
            </div>
          </div>

          {/* Certificate button — shown when all modules accessible */}
          {allAccessible && (
            <div className="mt-6 pt-5 border-t border-navy/[0.06]">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center">
                    <Award size={20} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-navy font-semibold text-sm">Certificate of Completion</p>
                    <p className="text-navy/40 text-xs">You have access to all modules</p>
                  </div>
                </div>
                <button
                  onClick={handleDownloadCertificate}
                  disabled={isGenerating}
                  data-testid="download-certificate-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-navy text-sm font-semibold rounded-lg hover:bg-gold-light disabled:opacity-50 transition-all shadow-sm shadow-gold/20"
                >
                  {isGenerating
                    ? <><div className="w-4 h-4 border-2 border-navy/30 border-t-navy rounded-full animate-spin" /> Generating...</>
                    : <><Download size={14} /> Download Certificate</>
                  }
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Module Cards ── */}
        <div className="space-y-3">
          {modules.map((module) => {
            const accessible = isModuleAccessible(module);
            return (
              <div
                key={module.id}
                className={`bg-white border rounded-xl p-6 transition-all duration-200 shadow-sm shadow-navy/[0.02] ${
                  accessible ? 'border-navy/[0.07] hover:border-gold/25' : 'border-navy/[0.05] opacity-80'
                }`}
                data-testid={`module-card-${module.id}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="relative flex-shrink-0">
                      <img
                        src={MODULE_ICONS[module.id - 1].url}
                        alt={MODULE_ICONS[module.id - 1].alt}
                        width={48}
                        height={48}
                        style={{ objectFit: 'contain', display: 'block', opacity: accessible ? 1 : 0.35 }}
                        draggable={false}
                      />
                      {!accessible && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-navy rounded-full flex items-center justify-center">
                          <Lock size={9} className="text-cream/50" />
                        </div>
                      )}
                      {accessible && completedIds.has(module.id) && (
                        <div
                          className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: '#C99316' }}
                          title="Completed"
                          data-testid={`completed-badge-${module.id}`}
                        >
                          <CheckCircle size={12} className="text-white" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h2 className="font-serif text-lg text-navy font-semibold mb-1">{module.title}</h2>
                      <p className="text-navy/45 text-sm leading-relaxed mb-3">{module.description}</p>
                      <div className="flex items-center gap-4 text-navy/25 text-xs">
                        <span className="flex items-center gap-1"><Clock size={11} /> {module.duration}</span>
                        <span className="flex items-center gap-1"><BookOpen size={11} /> {module.lessons} lessons</span>
                        {module.isFree && <span className="px-2 py-0.5 bg-green-500/10 text-green-700 rounded text-[10px] font-medium">FREE PREVIEW</span>}
                        {completedIds.has(module.id) && (
                          <span
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium"
                            style={{ backgroundColor: 'rgba(201,147,22,0.1)', color: '#C99316' }}
                            data-testid={`completed-tag-${module.id}`}
                          >
                            <CheckCircle size={9} /> Completed
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    {accessible ? (
                      <button
                        onClick={() => handleStartModule(module.id)}
                        className="px-5 py-2.5 bg-gold text-navy font-semibold rounded-lg hover:bg-gold-light transition-colors text-sm shadow-sm shadow-gold/15"
                        data-testid={`start-module-${module.id}`}
                      >
                        Start
                      </button>
                    ) : (
                      <div className="text-center min-w-[120px]">
                        <p className="text-navy/25 text-xs line-through mb-0.5">£20</p>
                        <p className="text-green-600 font-bold text-lg leading-none mb-2">£10</p>
                        <button
                          onClick={() => handlePurchaseModule(module.id)}
                          disabled={checkoutLoading === `module-${module.id}`}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-navy text-cream font-medium rounded-lg hover:bg-navy/90 transition-colors text-xs w-full disabled:opacity-60"
                          data-testid={`unlock-module-${module.id}`}
                        >
                          {checkoutLoading === `module-${module.id}`
                            ? <Loader2 size={11} className="animate-spin" />
                            : <Lock size={11} />
                          }
                          {checkoutLoading === `module-${module.id}` ? 'Redirecting…' : 'Unlock'}
                        </button>
                        <p className="flex items-center justify-center gap-1 text-[10px] text-navy/25 mt-1.5">
                          <ShieldCheck size={10} className="text-green-500" /> Stripe secure
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Partial access note */}
        {!allAccessible && (
          <p className="mt-5 text-center text-navy/30 text-xs">
            Unlock all 4 modules to earn your certificate of completion.
          </p>
        )}
      </div>
    </div>
  );
};

export default CourseDashboard;
